import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import get from 'lodash/get';
import { withRouter } from 'react-router-dom';
import VizSensor from 'react-visibility-sensor';
import { withStyles, createMuiTheme, responsiveFontSizes } from '@material-ui/core/styles';
import {
  LinearProgress,
  Typography,
  withWidth,
} from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import Immutable from 'seamless-immutable';
import union from 'lodash/union';
import flattenDeep from 'lodash/flattenDeep';
import LazyLoad from 'react-lazyload';

import { productIcons, SCROLL_TO_TOP_OFFSET } from '../../services/constants/Constants';
import styles from '../../css/menuPage/MenuPage.scss';
import ProductBoxComponent from './components/ProductBoxComponent';
import OrderFlowDialog from './components/OrderFlowDialog';
import OrderInfoBanner from './components/OrderInfoBanner';
import ProductFlowDialog from '../product/ProductFlowDialog';
import FloatingCartButton from './components/FloatingCartButton';
import ScrollToTop from '../core/components/ScrollToTop';

import TabBar from '../core/components/TabBar';
import Config from '../../../config.json';
import { getCurrentOrder, getIsOrderFlowDialogVisible } from '../../selectors';
import * as Actions from '../../actions/Actions';
import {
  checkBasicOrderInfo,
  compareValues,
  getAvailableCategories,
  getAvailableProducts,
  getMobileFriendlyStyle,
  groupTimeLimits,
  allowDialogClose,
} from '../../services/functions/Functions';
import * as FeatureFlags from '../../../feature-flags.json';
import * as ResponsiveStyles from '../../../jsonStyles/components/menuPage/MenuPage.style.json';

let theme = createMuiTheme();
const responsiveStyles = typeof styles === 'function' ? styles() : styles;
theme = responsiveFontSizes(theme);

responsiveStyles.pageContentLeftAlign = {
  ...responsiveStyles.pageContentLeftAlign,
  [theme.breakpoints.down('sm')]: {
    alignItems: ResponsiveStyles.pageContentLeftAlign.alignItems,
    padding: ResponsiveStyles.pageContentLeftAlign.padding,
  },
};

responsiveStyles.productsContainerLeftAlign = {
  ...responsiveStyles.productsContainerLeftAlign,
  [theme.breakpoints.down('sm')]: {
    flexDirection: ResponsiveStyles.productsContainerLeftAlign.flexDirection,
  },
};

responsiveStyles.pageContent = {
  ...responsiveStyles.pageContent,
  [theme.breakpoints.down('sm')]: ResponsiveStyles.pageContent,
};

responsiveStyles.outerContent = {
  ...responsiveStyles.outerContent,
  [theme.breakpoints.down('sm')]: {
    height: ResponsiveStyles.outerContent.height,
  },
};

responsiveStyles.categoryDescription = {
  ...responsiveStyles.categoryDescription,
  [theme.breakpoints.down('sm')]: ResponsiveStyles.categoryDescription,
};

responsiveStyles.categoryTitle = {
  ...responsiveStyles.categoryTitle,
  [theme.breakpoints.down('sm')]: ResponsiveStyles.categoryTitle,
};

responsiveStyles.timeLimit = {
  ...responsiveStyles.timeLimit,
  [theme.breakpoints.down('sm')]: ResponsiveStyles.timeLimit,
};

responsiveStyles.floatingButtonContainer = {
  ...responsiveStyles.floatingButtonContainer,
  [theme.breakpoints.down('sm')]: ResponsiveStyles.floatingButtonContainer,
};

const MenuItemProducts = (props) => {
  const {
    menuItemProducts,
    translation,
    currentOrder,
    goToProduct,
  } = props;

  return menuItemProducts.map(product => (
    <Grid
      item
      {...ResponsiveStyles.productBoxGrid}
      key={product.id}
    >
      <ProductBoxComponent
        translation={translation}
        product={product}
        handleClick={
            prodId => goToProduct(prodId)
          }
        currentOrderLocationId={(currentOrder && currentOrder.location) ? currentOrder.location.id : null}
      />
    </Grid>
  ));
};

class MenuPage extends Component {
  constructor(props) {
    super(props);

    this.state = {
      selectedCategoryId: props.categories
        ? this.getCategoriesTabs().length > 0 && this.getCategoriesTabs()[0].id
        : null,
      productId: null,
      showProductDialog: false,
      isBannerVisible: true,
    };
  }

  async componentDidMount() {
    const {
      user,
      actions,
      categories,
      currentOrder,
      currentCategoryId,
    } = this.props;
    const apiToken = user ? user.token : Config.general.apiKey;
    try {
      await actions.getAllResources(apiToken, ['products']);
      const response = await actions.getAllResources(apiToken, ['categories']);

      if (response.response && response.response[0]) {
        const responseCategories = response.response;
        const sortedCategories = responseCategories.sort(compareValues('position', 'asc'));
        this.setState({ selectedCategoryId: sortedCategories[0].id });
      }
    } catch (error) {
      console.log('API call error', error);
    }
    if (categories && categories.length > 0) this.setState({ selectedCategoryId: this.getCategoriesTabs()[0].id });
    if (!currentOrder) this.createEmptyOrder();
    if (currentCategoryId) this.setState({ selectedCategoryId: currentCategoryId });
    if (!allowDialogClose(currentOrder)) actions.showComponent('OrderFlowDialog');
  }

  // If user selects an invalid product, undo selection.
  componentDidUpdate(prevProps) {
    if (prevProps.currentOrder && prevProps.currentOrder !== this.props.currentOrder) {
      const availableCategoriesForOrder = this.getCategoriesTabs();
      const isValidCategory = this.checkIsValidCategorySelection(availableCategoriesForOrder);
      if (!isValidCategory) {
        this.setState({
          productId: null,
          selectedCategoryId: this.props.categories
            ? availableCategoriesForOrder.length > 0 && availableCategoriesForOrder[0].id
            : 0,
        });
      }
    }
  }

  onSelectCategory(categoryId) {
    this[`ref-${categoryId}`].scrollIntoView();
    // Wait until scroll is complete
    setTimeout(() => {
      this.setState({ selectedCategoryId: categoryId });
    }, 300);
  }

  // Generate list of all relevant icons for the page
  getIconList = products => union(flattenDeep(products.map(prod => prod.icons)));

  getProductsFromCategory(categoryId) {
    const { products, categories, currentOrder } = this.props;
    if (!products || !categories) return [];
    let availableProducts = products.filter(product => product.category.id === categoryId);
    availableProducts = getAvailableProducts(availableProducts, currentOrder);

    return availableProducts.filter(product => !product.isDisabled && !product.isDeleted);
  }

  getCategoriesTabs() {
    const { categories, currentOrder } = this.props;
    if (!categories) return [];
    const mutableCategories = Immutable.asMutable(categories, { deep: true });
    let availableCategoriesForOrder = getAvailableCategories(mutableCategories, currentOrder);
    availableCategoriesForOrder = availableCategoriesForOrder.sort(compareValues('position', 'asc'));
    return availableCategoriesForOrder.map(category => ({ id: category.id, label: category.name }));
  }

  /**
   * Return rendered time limits if specified for the category
   * @param {*} categoryId the id of the selected category
   */
  getTimeLimits = (categoryId) => {
    const {
      categories,
      translation,
      classes,
      width,
    } = this.props;
    const subTextStyle = classes.timeLimit;
    const selectedCategory = categories.find(cat => cat.id === categoryId);
    const mobileFriendlyStyleProps = { classes, width };
    const timeLimitContainerStyles = getMobileFriendlyStyle(mobileFriendlyStyleProps, 'timeLimitContainer');

    if (selectedCategory && selectedCategory.timeLimits) {
      const { timeLimits } = selectedCategory;

      return (
        timeLimits.length > 0 && timeLimits[0].start
          ? (
            <div className={timeLimitContainerStyles}>
              <Typography className={classes.timeLimit}>
                {`${translation('MenuPage.timeLimit')}:`}
              </Typography>
              {groupTimeLimits(timeLimits, subTextStyle, this.props)}
            </div>
          ) : null
      );
    }
    return null;
  }

  getSelectedCategoryDetails = (selectedCategoryId, property) => {
    const { categories } = this.props;
    const selectedCategory = categories && categories.find(cat => cat.id === selectedCategoryId);
    const shouldReturnProperty = (
      selectedCategory && (
        property !== 'description'
        || (property === 'description' && FeatureFlags.MenuPage.showCategoryDescription)
      )
    );
    return shouldReturnProperty && (selectedCategory[property] || '');
  }

  getProductList = (selectedCategoryId) => {
    const filteredProducts = this.getProductsFromCategory(selectedCategoryId);
    const mutableProducts = Immutable.asMutable(filteredProducts, { deep: true });
    // Sort products by position
    return mutableProducts.sort((a, b) => a.position - b.position);
  }

  getFullList = () => {
    const { products } = this.props;
    if (!products) return [];
    const availableCategoriesForOrder = this.getCategoriesTabs();
    return availableCategoriesForOrder.map(category => ({
      categoryId: category.id,
      products: this.getProductList(category.id),
    }));
  }

  generateKey = index => (`key${index}`);

  handleClickBannerLink = () => {
    const { actions } = this.props;
    actions.toggleComponent('OrderFlowDialog');
  }

  toggleProductDialog = (prodId) => {
    const { showProductDialog } = this.state;
    const { currentOrder, actions } = this.props;
    if (FeatureFlags.MenuPage.showNewFlow && !checkBasicOrderInfo(currentOrder)) {
      actions.toggleComponent('OrderFlowDialog');
      this.setState({ productId: prodId });
    } else {
      this.setState({ showProductDialog: !showProductDialog, productId: prodId });
    }
  }

  goToProduct = (prodId) => {
    const { currentOrder, actions } = this.props;

    if (FeatureFlags.MenuPage.showNewFlow && !checkBasicOrderInfo(currentOrder)) {
      actions.toggleComponent('OrderFlowDialog');
      this.setState({ productId: prodId });
    } else {
      this.toggleProductDialog(prodId);
    }
  }

  toggleOrderDialog() {
    const { actions } = this.props;
    actions.toggleComponent('OrderFlowDialog');
  }

  hideOrderDialog() {
    const { actions } = this.props;
    actions.hideComponent('OrderFlowDialog');
  }

  checkIsValidCategorySelection(availableCategoriesForOrder) {
    const { products, categories } = this.props;
    const { productId, selectedCategoryId } = this.state;
    if (productId) {
      const product = products.find(prod => prod.id === productId);
      if (!product) return false;
    } else if (selectedCategoryId) {
      const category = categories.find(cat => cat.id === selectedCategoryId);
      if (!category) return false;
      return (availableCategoriesForOrder.some(cat => cat.id === selectedCategoryId));
    }
    return true;
  }

  createEmptyOrder() {
    const { actions } = this.props;
    actions.createOrder(null, {
    });
  }

  renderLegend = (products) => {
    const { translation, classes } = this.props;
    const iconList = this.getIconList(products);
    if (!iconList) return null;
    const iconObjects = productIcons.filter(icon => iconList.includes(icon.key));
    if (!iconObjects) return null;
    return (
      <div className={classes.legendContainer}>
        {
          iconObjects.map(prodIcon => (
            <div key={prodIcon.key} className={classes.legend}>
              <img
                src={prodIcon.icon}
                alt={translation(prodIcon.translation)}
                className={classes.productIcon}
              />
              <Typography className={classes.legendText}>
                {`- ${translation(prodIcon.translation)}`}
              </Typography>
            </div>
            ))
        }
      </div>
    );
  }

  render() {
    const {
      loading,
      classes,
      translation,
      user,
      actions,
      currentOrder,
      history,
      width,
      stateVisibleOrderFlowDialog,
    } = this.props;
    const {
      selectedCategoryId, productId, showProductDialog, isBannerVisible,
    } = this.state;
    const fullList = this.getFullList();
    // Sort products by position
    const showTabBarMenu = selectedCategoryId && FeatureFlags.MenuPage && FeatureFlags.MenuPage.showTabBarMenu;
    const mobileFriendlyStyleProps = { classes, width };
    const categoryTitleContainerStyles = getMobileFriendlyStyle(mobileFriendlyStyleProps, 'categoryTitleContainer');
    const allowDialogToClose = allowDialogClose(currentOrder) === true;
    return (
      <div>
        {
          (stateVisibleOrderFlowDialog || !allowDialogToClose)
          && (
            <OrderFlowDialog
              open={stateVisibleOrderFlowDialog || !allowDialogToClose}
              user={user}
              actions={actions}
              handleClose={() => this.hideOrderDialog()}
              translation={translation}
              currentOrderId={get(currentOrder, 'id')}
              history={history}
              productId={productId}
              loading={loading}
              toggleProductDialog={this.toggleProductDialog}
            />
          )
        }
        {
          showProductDialog
          && (
            <ProductFlowDialog
              open={showProductDialog}
              user={user}
              actions={actions}
              handleClose={this.toggleProductDialog}
              translation={translation}
              history={history}
              loading={loading}
              productId={productId}
            />
          )
        }
        {
          <div className={classes.outerContent}>
            <div className={classes.banners}>
              {
                FeatureFlags.MenuPage.showBannner
                  && (
                    <VizSensor
                      onChange={(isVisible) => {
                        this.setState({ isBannerVisible: isVisible });
                      }}
                    >
                      <OrderInfoBanner
                        translation={translation}
                        handleClick={() => this.handleClickBannerLink()}
                      />
                    </VizSensor>
                  )
              }
              {
                showTabBarMenu
                  && (
                    <TabBar
                      type="category"
                      stickToTop={!isBannerVisible}
                      selected={selectedCategoryId}
                      onClickSelected={value => this.onSelectCategory(value)}
                      tabs={this.getCategoriesTabs()}
                      translation={translation}
                    />
                  )
              }
            </div>
            {
              FeatureFlags.MenuPage.showMenuDescription
                && (
                  <Typography className={classes.summary}>
                    {translation('MenuPage.description')}
                  </Typography>
                )
            }
            {
              loading !== 0 && (
                <LinearProgress />
              )
            }
            <div className={classes.pageContent}>
              {
                fullList.map((menuItem, i) => {
                  const menuItemProducts = menuItem.products || [];
                  let categoryTitle = this.getSelectedCategoryDetails(menuItem.categoryId, 'name');
                  if (FeatureFlags.MenuPage.useUppercaseCategoryName) categoryTitle = categoryTitle.toUpperCase();
                  return (
                    <div
                      // eslint-disable-next-line no-return-assign
                      ref={ref => this[`ref-${menuItem.categoryId}`] = ref}
                      key={this.generateKey(i)}
                    >
                      <LazyLoad
                        offset={100}
                      >
                        <Fragment>
                          <div className={categoryTitleContainerStyles}>
                            <Typography className={classes.categoryTitle}>
                              {categoryTitle}
                            </Typography>
                            <Typography className={classes.categoryDescription}>
                              {this.getSelectedCategoryDetails(menuItem.categoryId, 'description')}
                            </Typography>
                            {this.getTimeLimits(menuItem.categoryId)}
                          </div>
                          <VizSensor
                            partialVisibility
                            offset={{ top: 300, bottom: 300 }}
                            onChange={(isVisible) => {
                              if (isVisible) this.setState({ selectedCategoryId: menuItem.categoryId });
                            }}
                          >
                            <Grid
                              container
                              className={classes.gridContainer}
                              {...ResponsiveStyles.gridContainer}
                            >
                              <MenuItemProducts
                                menuItemProducts={menuItemProducts}
                                translation={translation}
                                currentOrder={currentOrder}
                                toggleProductDialog={this.toggleProductDialog}
                                handleClickOpenProductPage={this.handleClickOpenProductPage}
                                goToProduct={this.goToProduct}
                              />
                            </Grid>
                          </VizSensor>
                        </Fragment>
                      </LazyLoad>
                      {this.renderLegend(menuItemProducts)}
                    </div>
                  );
                })
              }
            </div>
          </div>
        }
        {
          !isBannerVisible
          && (
            <div className={classes.floatingButtonContainer}>
              <FloatingCartButton />
              <ScrollToTop showBelow={SCROLL_TO_TOP_OFFSET} />
            </div>
          )
        }
      </div>
    );
  }
}

MenuPage.propTypes = {
  actions: PropTypes.objectOf(PropTypes.func).isRequired,
  loading: PropTypes.number.isRequired,
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  translation: PropTypes.func.isRequired,
  history: PropTypes.objectOf(PropTypes.any).isRequired,
  products: PropTypes.arrayOf(PropTypes.any).isRequired,
  categories: PropTypes.arrayOf(PropTypes.any),
  user: PropTypes.objectOf(PropTypes.any),
  currentOrder: PropTypes.objectOf(PropTypes.any).isRequired,
  currentCategoryId: PropTypes.number,
  width: PropTypes.string,
};

MenuPage.defaultProps = {
  user: null,
  categories: undefined,
  currentCategoryId: null,
  width: '',
};

const mapStateToProps = state => ({
  currentOrder: getCurrentOrder(state),
  stateVisibleOrderFlowDialog: getIsOrderFlowDialogVisible(state),
});

const mapDispatchToProps = dispatch => ({
  createOrder: (user, order) => dispatch(Actions.createOrder(user, order)),
});

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(responsiveStyles)(withRouter(withWidth()(MenuPage))));
